import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/data';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';

export default function Article() {
  const { id } = useParams<{ id: string }>();

  const article = useMemo(
    () => articles.find((a) => a.id === id),
    [id]
  );

  const relatedArticles = useMemo(
    () =>
      article
        ? articles.filter((a) => a.id !== article.id && a.category === article.category).slice(0, 3)
        : [],
    [article]
  );

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 欲火视频影视资讯`;
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.setAttribute('href', `${window.location.origin}/article/${id}`);
    }
  }, [article, id]);

  if (!article) {
    return (
      <main className="w-full px-4 md:px-6 max-w-7xl mx-auto py-20 text-center">
        <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-200">
          <i className="ri-article-line text-2xl text-foreground-400"></i>
        </div>
        <h1 className="text-xl font-bold text-foreground-950 mb-2">文章未找到</h1>
        <p className="text-sm text-foreground-600 mb-6">该文章可能已被删除或链接无效</p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
          返回首页
        </Link>
      </main>
    );
  }

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.publishDate,
    image: article.coverImage,
    publisher: {
      '@type': 'Organization',
      name: '欲火视频',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${window.location.origin}/article/${id}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: window.location.origin + '/' },
      { '@type': 'ListItem', position: 2, name: article.title },
    ],
  };

  return (
    <main>
      <Breadcrumb
        items={[{ name: article.title }]}
      />

      <article className="w-full px-4 md:px-6 max-w-4xl mx-auto pb-16">
        <header className="mb-8">
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold font-heading text-foreground-950 leading-tight mb-4">
            {article.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-600">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line"></i>
              {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              {article.publishDate}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line"></i>
              阅读时间 {article.readTime}
            </span>
          </div>
        </header>

        <div className="aspect-[2/1] rounded-xl overflow-hidden mb-10">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover"
            title={`${article.title} - 欲火视频影视资讯`}
          />
        </div>

        <div
          className="prose prose-lg max-w-none
            prose-headings:font-heading prose-headings:text-foreground-950
            prose-h2:text-xl prose-h2:font-bold prose-h2:mt-10 prose-h2:mb-4
            prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-5 prose-p:text-base
            prose-strong:text-foreground-900"
          dangerouslySetInnerHTML={{ __html: article.content }}
        />

        <footer className="mt-12 pt-8 border-t border-background-200">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="text-sm text-foreground-600">分享到：</span>
              <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200 text-foreground-600 hover:bg-primary-500 hover:text-white transition-all cursor-pointer" aria-label="分享到微博">
                <i className="ri-weibo-line"></i>
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200 text-foreground-600 hover:bg-primary-500 hover:text-white transition-all cursor-pointer" aria-label="分享到微信">
                <i className="ri-wechat-line"></i>
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200 text-foreground-600 hover:bg-primary-500 hover:text-white transition-all cursor-pointer" aria-label="复制链接">
                <i className="ri-link"></i>
              </button>
            </div>
            <Link
              to="/"
              className="flex items-center gap-1.5 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </footer>
      </article>

      {relatedArticles.length > 0 && (
        <section className="w-full px-4 md:px-6 max-w-7xl mx-auto pb-16">
          <h2 className="text-xl font-bold font-heading text-foreground-950 mb-6">
            相关文章
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {relatedArticles.map((ra) => (
              <ArticleCard
                key={ra.id}
                id={ra.id}
                title={ra.title}
                coverImage={ra.coverImage}
                summary={ra.summary}
                category={ra.category}
                author={ra.author}
                publishDate={ra.publishDate}
                readTime={ra.readTime}
              />
            ))}
          </div>
        </section>
      )}

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
    </main>
  );
}